import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Sparkles, RefreshCw, TrendingUp, Dumbbell, UtensilsCrossed } from 'lucide-react';

export function ContinueProgramCard() {
  const { refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const load = async () => {
    try { const r = await api.get('/programs/continue-info'); setInfo(r.data); return r.data; }
    catch { setInfo(null); return null; }
  };

  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, []);

  // While generating, poll until the block is ready, then refresh the user + reload page data.
  useEffect(() => {
    if (info?.generating) {
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        const d = await load();
        if (d && !d.generating) {
          clearInterval(pollRef.current);
          await refresh();
          toast.success('Your next 4-week block is ready!');
          setTimeout(() => window.location.reload(), 600);
        }
      }, 5000);
    }
    return () => clearInterval(pollRef.current);
  }, [info?.generating]);

  const start = async () => {
    setStarting(true);
    try {
      await api.post('/programs/continue');
      toast.success('Generating your next block — this takes ~30–60s.');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start. Please try again.');
    } finally { setStarting(false); }
  };

  if (!info?.eligible) return null;

  if (info.generating) {
    return (
      <div className="regen-card-elevated p-5 border border-[var(--c-green-soft)]" data-testid="continue-program-generating">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[var(--c-green-soft)] flex items-center justify-center">
            <RefreshCw className="w-5 h-5 text-[var(--c-green-ink)] animate-spin" />
          </div>
          <div>
            <div className="font-display font-bold text-sm">Building your next 4-week block…</div>
            <div className="text-xs text-muted-foreground mt-0.5">Progressing your training & nutrition from your results. This can take up to a minute.</div>
          </div>
        </div>
      </div>
    );
  }

  if (!info.has_previous_block || info.day_of_block === 0) return null;

  const dayLine = info.day_of_block ? `Day ${info.day_of_block} of ${info.block_length_days}` : null;

  return (
    <div className="regen-card-elevated p-5 relative overflow-hidden border border-[var(--c-green-soft)] bg-gradient-to-br from-[var(--c-green-soft)] to-transparent" data-testid="continue-program-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[var(--c-green-soft)] flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-[var(--c-green-ink)]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-black text-lg">Continue your progress</div>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Generate your next 4-week block — periodized to progress from your logged results with heavier
            training and fresh, varied meals.
          </p>
          {dayLine && <div className="text-[11px] text-[var(--c-green-ink)] mt-2 font-mono">{dayLine} of your current block</div>}

          {(info.workout_adherence != null || info.meal_adherence != null || info.weight_line) && (
            <div className="flex flex-wrap gap-2 mt-3">
              {info.workout_adherence != null && (
                <span className="pill bg-[#00C8FF]/10 text-[#00C8FF] text-[11px] flex items-center gap-1" data-testid="continue-workout-adherence">
                  <Dumbbell className="w-3 h-3" /> {info.workout_adherence}% workouts
                </span>
              )}
              {info.meal_adherence != null && (
                <span className="pill bg-[#FFB800]/10 text-[#FFB800] text-[11px] flex items-center gap-1" data-testid="continue-meal-adherence">
                  <UtensilsCrossed className="w-3 h-3" /> {info.meal_adherence}% meals
                </span>
              )}
              {info.weight_line && (
                <span className="pill bg-[var(--c-green-soft)] text-[var(--c-green-ink)] text-[11px] flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" /> {info.weight_line}
                </span>
              )}
            </div>
          )}

          <Button onClick={start} disabled={starting} data-testid="continue-program-btn"
            className="mt-4 h-11 px-5 bg-[var(--c-green)] hover:bg-[var(--c-green-soft)] text-[#081726] font-semibold rounded-xl disabled:opacity-50">
            {starting ? 'Starting…' : <><Sparkles className="w-4 h-4 mr-2" /> Generate next block</>}
          </Button>
          {!info.can_continue_now && (
            <div className="text-[10px] text-muted-foreground mt-2" data-testid="continue-early-hint">
              Best used in the final week of your current block, but you can start a fresh block anytime.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
